"use client";

import { useState } from "react";
import {
  LANGUAGES,
  PROFICIENCY_LEVELS,
  PROFICIENCY_LABELS,
  type LanguageEntry,
} from "@/lib/taxonomy";

// ═══ CANDIDATE MODE — Select languages + proficiency ═══

export function LanguagePicker({
  value,
  onChange,
}: {
  value: LanguageEntry[];
  onChange: (entries: LanguageEntry[]) => void;
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState("");

  const addedLanguages = new Set(value.map((e) => e.language));
  const filtered = LANGUAGES.filter(
    (l) =>
      !addedLanguages.has(l) &&
      l.toLowerCase().includes(search.toLowerCase())
  );

  function addLanguage(lang: string) {
    onChange([...value, { language: lang, proficiency: "conversational" }]);
    setSearch("");
    setShowAdd(false);
  }

  function removeLanguage(lang: string) {
    onChange(value.filter((e) => e.language !== lang));
  }

  function updateProficiency(
    lang: string,
    proficiency: LanguageEntry["proficiency"]
  ) {
    onChange(
      value.map((e) =>
        e.language === lang ? { ...e, proficiency } : e
      )
    );
  }

  return (
    <div>
      {/* Added languages */}
      <div className="flex flex-col gap-2 mb-3">
        {value.map((entry) => (
          <div
            key={entry.language}
            className="flex items-center gap-2 bg-warm-bg rounded-[10px] px-3 py-2 border border-border-light"
          >
            <span className="text-[13px] font-medium flex-1">
              {entry.language}
            </span>
            <select
              value={entry.proficiency}
              onChange={(e) =>
                updateProficiency(
                  entry.language,
                  e.target.value as LanguageEntry["proficiency"]
                )
              }
              className="text-[12px] font-medium bg-white border border-border rounded-sm px-2 py-1 outline-none focus:border-gold font-sans"
            >
              {PROFICIENCY_LEVELS.map((p) => (
                <option key={p} value={p}>
                  {PROFICIENCY_LABELS[p]}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => removeLanguage(entry.language)}
              className="w-6 h-6 flex items-center justify-center rounded-full text-text-muted hover:text-error hover:bg-error/10 transition-colors bg-transparent border-none cursor-pointer text-sm"
              aria-label={`Remove ${entry.language}`}
            >
              &times;
            </button>
          </div>
        ))}
      </div>

      {/* Add language */}
      {showAdd ? (
        <div className="border border-border-light rounded-[10px] bg-white p-2">
          <input
            type="text"
            placeholder="Search languages..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-3 py-2 text-[13px] border-none outline-none bg-transparent font-sans"
            autoFocus
          />
          <div className="max-h-36 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-[12px] text-text-muted px-3 py-2">
                No languages found
              </p>
            ) : (
              filtered.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => addLanguage(lang)}
                  className="w-full text-left px-3 py-2 text-[13px] rounded-sm hover:bg-warm-bg transition-colors bg-transparent border-none cursor-pointer font-sans"
                >
                  {lang}
                </button>
              ))
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setShowAdd(false);
              setSearch("");
            }}
            className="w-full text-[11px] text-text-muted mt-1 pt-1 border-t border-border-light bg-transparent border-x-0 border-b-0 cursor-pointer font-sans"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="text-[12px] font-medium text-gold hover:text-gold-light transition-colors bg-transparent border-none cursor-pointer font-sans flex items-center gap-1"
        >
          + Add language
        </button>
      )}
    </div>
  );
}

// ═══ EMPLOYER FILTER MODE — Filter by language + minimum proficiency ═══

export interface LanguageFilter {
  language: string;
  minProficiency: LanguageEntry["proficiency"] | "any";
}

export function LanguageFilterPicker({
  value,
  onChange,
}: {
  value: LanguageFilter[];
  onChange: (filters: LanguageFilter[]) => void;
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState("");

  const addedLanguages = new Set(value.map((f) => f.language));
  const filtered = LANGUAGES.filter(
    (l) =>
      !addedLanguages.has(l) &&
      l.toLowerCase().includes(search.toLowerCase())
  );

  function addLanguage(lang: string) {
    onChange([...value, { language: lang, minProficiency: "any" }]);
    setSearch("");
    setShowAdd(false);
  }

  function removeLanguage(lang: string) {
    onChange(value.filter((f) => f.language !== lang));
  }

  function updateMinProficiency(
    lang: string,
    minProficiency: LanguageFilter["minProficiency"]
  ) {
    onChange(
      value.map((f) =>
        f.language === lang ? { ...f, minProficiency } : f
      )
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-2 mb-3">
        {value.map((filter) => (
          <div
            key={filter.language}
            className="flex items-center gap-2 bg-warm-bg rounded-[10px] px-3 py-2 border border-border-light"
          >
            <span className="text-[13px] font-medium flex-1">
              {filter.language}
            </span>
            <select
              value={filter.minProficiency}
              onChange={(e) =>
                updateMinProficiency(
                  filter.language,
                  e.target.value as LanguageFilter["minProficiency"]
                )
              }
              className="text-[12px] font-medium bg-white border border-border rounded-sm px-2 py-1 outline-none focus:border-gold font-sans"
            >
              <option value="any">Any level</option>
              {PROFICIENCY_LEVELS.map((p) => (
                <option key={p} value={p}>
                  {PROFICIENCY_LABELS[p]}+
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => removeLanguage(filter.language)}
              className="w-6 h-6 flex items-center justify-center rounded-full text-text-muted hover:text-error hover:bg-error/10 transition-colors bg-transparent border-none cursor-pointer text-sm"
              aria-label={`Remove ${filter.language}`}
            >
              &times;
            </button>
          </div>
        ))}
      </div>

      {showAdd ? (
        <div className="border border-border-light rounded-[10px] bg-white p-2">
          <input
            type="text"
            placeholder="Search languages..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-3 py-2 text-[13px] border-none outline-none bg-transparent font-sans"
            autoFocus
          />
          <div className="max-h-36 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-[12px] text-text-muted px-3 py-2">
                No languages found
              </p>
            ) : (
              filtered.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => addLanguage(lang)}
                  className="w-full text-left px-3 py-2 text-[13px] rounded-sm hover:bg-warm-bg transition-colors bg-transparent border-none cursor-pointer font-sans"
                >
                  {lang}
                </button>
              ))
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setShowAdd(false);
              setSearch("");
            }}
            className="w-full text-[11px] text-text-muted mt-1 pt-1 border-t border-border-light bg-transparent border-x-0 border-b-0 cursor-pointer font-sans"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="text-[12px] font-medium text-gold hover:text-gold-light transition-colors bg-transparent border-none cursor-pointer font-sans flex items-center gap-1"
        >
          + Add language filter
        </button>
      )}
    </div>
  );
}

// ═══ PROFICIENCY MATCHING UTILITY ═══

const PROFICIENCY_RANK: Record<string, number> = {
  basic: 1,
  conversational: 2,
  fluent: 3,
  native: 4,
};

/** Check if a candidate's language entries match an employer's filter */
export function matchesLanguageFilter(
  candidateLanguages: LanguageEntry[],
  filters: LanguageFilter[]
): boolean {
  if (filters.length === 0) return true;
  return filters.every((filter) => {
    const match = candidateLanguages.find(
      (cl) => cl.language === filter.language
    );
    if (!match) return false;
    if (filter.minProficiency === "any") return true;
    return (
      PROFICIENCY_RANK[match.proficiency] >=
      PROFICIENCY_RANK[filter.minProficiency]
    );
  });
}
